import { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { ProductCardProps } from "../../types/types";
import { fetchFavoriteProducts, fetchLocalFavoriteProducts, removeFavorite, syncLocalFavorites, addFavorite, getFavorites } from "./favorutes";

// Utility to debounce function calls
const debounce = <F extends (...args: any[]) => any>(func: F, wait: number) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<F>): Promise<ReturnType<F>> => {
    return new Promise((resolve, reject) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(async () => {
        try {
          const result = await func(...args);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      }, wait);
    });
  };
};

interface FavoriteContextType {
  favorites: ProductCardProps[];
  isLoggedIn: boolean;
  errorMessage: string | null;
  addFavorite: (productId: number) => Promise<void>;
  removeFavorite: (productId: number) => Promise<void>;
  loadFavorites: () => Promise<void>;
}

const FavoriteContext = createContext<FavoriteContextType | undefined>(undefined);

export const FavoriteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<ProductCardProps[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("access");
    const newIsLoggedIn = !!token;
    setIsLoggedIn(newIsLoggedIn);
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    const token = localStorage.getItem("access");
    const newIsLoggedIn = !!token;
    setIsLoggedIn(newIsLoggedIn);
    setErrorMessage(null);

    // console.error('Loading favorites, isLoggedIn:', newIsLoggedIn);
    if (newIsLoggedIn) {
      try {
        const products = await fetchFavoriteProducts();
        setFavorites(products);
        // console.error("API Favorites:", products.map(p => ({ id: p.id, feature: p.feature })));

        const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
        // console.error('Local favorites before sync:', localFavorites);
        if (Array.isArray(localFavorites) && localFavorites.every(id => typeof id === "number")) {
          try {
            await syncLocalFavorites(localFavorites, products.map(p => p.id));
            localStorage.setItem("liked", JSON.stringify([]));
            // console.error('Local favorites synced and cleared');
            // Refresh favorites after sync
            const updatedProducts = await fetchFavoriteProducts();
            setFavorites(updatedProducts);
          } catch (e) {
            console.error("Failed to sync local favorites:", e);
            setErrorMessage("Failed to sync local favorites. Using local storage.");
          }
        } else {
          console.warn("Invalid local favorites, resetting to []");
          localStorage.setItem("liked", JSON.stringify([]));
        }
      } catch (e: any) {
        console.error("Failed to fetch favorites from API:", {
          message: e.message,
          status: e.response?.status,
          url: e.config?.url,
        });
        if (e.response?.status === 404) {
          console.warn("Favorites endpoint not found. Ensure backend endpoint is /api/v1/favorite/.");
          setErrorMessage("Favorites feature is currently unavailable. Using local storage.");
        }
        const localFavorites = await fetchLocalFavoriteProducts();
        // console.error('Loaded local favorites:', localFavorites);
        setFavorites(localFavorites);
      }
    } else {
      const localFavorites = await fetchLocalFavoriteProducts();
      // console.error('Loaded local favorites (not logged in):', localFavorites);
      setFavorites(localFavorites);
    }
  };

  const debouncedAddFavorite = useCallback(
    debounce(async (productId: number) => {
      setErrorMessage(null);
      if (isLoggedIn) {
        try {
          await addFavorite(productId);
          const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
          if (!localFavorites.includes(productId)) {
            localFavorites.push(productId);
            localStorage.setItem("liked", JSON.stringify(localFavorites));
          }
          // Update favorites directly instead of calling loadFavorites
          const updatedFavorites = await fetchFavoriteProducts();
          setFavorites(updatedFavorites);
        } catch (e: any) {
          console.error(`Failed to add favorite ${productId}:`, e);
          setErrorMessage("Failed to add favorite. Please try again.");
          // Fallback to local storage
          const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
          if (!localFavorites.includes(productId)) {
            localFavorites.push(productId);
            localStorage.setItem("liked", JSON.stringify(localFavorites));
            const updatedFavorites = await fetchLocalFavoriteProducts();
            setFavorites(updatedFavorites);
          }
        }
      } else {
        const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
        if (!localFavorites.includes(productId)) {
          localFavorites.push(productId);
          localStorage.setItem("liked", JSON.stringify(localFavorites));
          const updatedFavorites = await fetchLocalFavoriteProducts();
          setFavorites(updatedFavorites);
        }
      }
    }, 300),
    [isLoggedIn]
  );

  const addFavoriteHandler = async (productId: number): Promise<void> => {
    await debouncedAddFavorite(productId);
  };

 const removeFavoriteHandler = useCallback(
  async (productId: number) => {
    setErrorMessage(null);
    if (isLoggedIn) {
      try {
        const apiFavorites = await getFavorites();
        const favoriteToRemove = apiFavorites.find((item) => item.product === productId);
        if (favoriteToRemove) {
          await removeFavorite(favoriteToRemove.id);
          const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
          const updatedLocalFavorites = localFavorites.filter((id: number) => id !== productId);
          localStorage.setItem("liked", JSON.stringify(updatedLocalFavorites));
          setFavorites((prevFavorites) => prevFavorites.filter((item) => item.id !== productId));
        } else {
          console.warn(`No favorite found for productId ${productId}`);
          setErrorMessage("Favorite not found.");
        }
      } catch (e: any) {
        console.error("Failed to remove favorite from API:", e);
        setErrorMessage("Failed to remove favorite. Updating locally.");
        // Fallback to local storage
        const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
        if (Array.isArray(localFavorites) && localFavorites.every(id => typeof id === "number")) {
          const updatedLocalFavorites = localFavorites.filter((id: number) => id !== productId);
          localStorage.setItem("liked", JSON.stringify(updatedLocalFavorites));
          const updatedFavorites = await fetchLocalFavoriteProducts();
          setFavorites(updatedFavorites);
        } else {
          localStorage.setItem("liked", JSON.stringify([]));
          setFavorites([]);
        }
      }
    } else {
      const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
      if (Array.isArray(localFavorites) && localFavorites.every(id => typeof id === "number")) {
        const updatedLocalFavorites = localFavorites.filter((id: number) => id !== productId);
        localStorage.setItem("liked", JSON.stringify(updatedLocalFavorites));
        const updatedFavorites = await fetchLocalFavoriteProducts();
        setFavorites(updatedFavorites);
      } else {
        localStorage.setItem("liked", JSON.stringify([]));
        setFavorites([]);
      }
    }
  },
  [favorites, isLoggedIn]
);

  return (
    <FavoriteContext.Provider
      value={{
        favorites,
        isLoggedIn,
        errorMessage,
        addFavorite: addFavoriteHandler,
        removeFavorite: removeFavoriteHandler,
        loadFavorites,
      }}
    >
      {children}
    </FavoriteContext.Provider>
  );
};

export const useFavorite = () => {
  const context = useContext(FavoriteContext);
  if (!context) {
    throw new Error("useFavorite must be used within a FavoriteProvider");
  }
  return context;
};